import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { clearToken } from "auth/token";
import { useTranslation } from "react-i18next";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import config from "../../../../config";

function SectionCard({ title, subtitle, children }) {
  return (
    <Card>
      <CardContent>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h6" fontWeight={900} sx={{ color: "#fff" }}>
              {title}
            </Typography>
            {subtitle ? (
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)" }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
        </Stack>
        <Divider sx={{ my: 2 }} />
        {children}
      </CardContent>
    </Card>
  );
}

function StatusBadge({ label, color }) {
  const colors = {
    green: { bg: "rgba(76,175,80,0.15)", border: "rgba(76,175,80,0.4)", text: "#81c784" },
    orange: { bg: "rgba(255,152,0,0.15)", border: "rgba(255,152,0,0.4)", text: "#ffb74d" },
    red: { bg: "rgba(244,67,54,0.12)", border: "rgba(244,67,54,0.35)", text: "#e57373" },
    blue: { bg: "rgba(33,150,243,0.12)", border: "rgba(33,150,243,0.35)", text: "#64b5f6" },
    grey: { bg: "rgba(255,255,255,0.08)", border: "rgba(255,255,255,0.2)", text: "rgba(255,255,255,0.7)" },
  };
  const c = colors[color] || colors.grey;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        px: 1.5,
        py: 0.4,
        borderRadius: 1,
        fontSize: "0.75rem",
        fontWeight: 700,
        bgcolor: c.bg,
        border: `1px solid ${c.border}`,
        color: c.text,
      }}
    >
      {label}
    </Box>
  );
}

function InfoRow({ label, value, badge }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        gap: 1,
        alignItems: { xs: "flex-start", sm: "center" },
        py: 1.5,
        borderBottom: "1px solid rgba(255,255,255,0.07)",
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <Typography
        variant="body2"
        sx={{ color: "rgba(255,255,255,0.5)", minWidth: 180, flexShrink: 0 }}
      >
        {label}
      </Typography>
      {badge ? badge : (
        <Typography variant="body2" sx={{ color: "#fff", fontWeight: 500 }}>
          {value || "—"}
        </Typography>
      )}
    </Box>
  );
}

function _formatDate(date, locale = "el-GR") {
  if (!date) return "—";
  return new Date(date).toLocaleDateString(locale, {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

const BILLING_STATUS_LABELS = {
  active: { labelKey: "billing.billingStatus.active", color: "green" },
  trialing: { labelKey: "billing.billingStatus.trialing", color: "blue" },
  past_due: { labelKey: "billing.billingStatus.past_due", color: "red" },
  unpaid: { labelKey: "billing.billingStatus.unpaid", color: "red" },
  canceled: { labelKey: "billing.billingStatus.canceled", color: "grey" },
  incomplete: { labelKey: "billing.billingStatus.incomplete", color: "orange" },
  none: { labelKey: "billing.billingStatus.none", color: "grey" },
};

const ACCOUNT_STATUS_LABELS = {
  visible: { labelKey: "billing.accountStatus.visible", color: "green" },
  toBeArchived: { labelKey: "billing.accountStatus.toBeArchived", color: "orange" },
  grace: { labelKey: "billing.accountStatus.grace", color: "orange" },
  suspended: { labelKey: "billing.accountStatus.suspended", color: "red" },
  archived: { labelKey: "billing.accountStatus.archived", color: "grey" },
  blocked: { labelKey: "billing.accountStatus.blocked", color: "red" },
};

export default function BillingPage() {
  const token = useMemo(() => localStorage.getItem("admin_jwt"), []);
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === "en" ? "en-GB" : "el-GR";
  const formatDate = (date) => _formatDate(date, dateLocale);
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    name: "",
    parentCategory: "",
    accountStatus: "",
    subscribeUntil: "",
    billingStatus: "none",
    stripeSubscriptionId: null,
    stripePriceId: "",
    stripeCancelAtPeriodEnd: false,
    currentPlanKey: null,
    pendingPlanKey: null,
    pendingSwitchDate: null,
  });
  const [plans, setPlans] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [planKey, setPlanKey] = useState("y3");
  const [switchPlanKey, setSwitchPlanKey] = useState(null);
  const [switching, setSwitching] = useState(false);
  const [renewing, setRenewing] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [terminating, setTerminating] = useState(false);
  const [terminateModalOpen, setTerminateModalOpen] = useState(false);
  const [terminateConfirmText, setTerminateConfirmText] = useState("");
  const [toast, setToast] = useState({ open: false, type: "success", msg: "" });

  const notify = (type, msg) => setToast({ open: true, type, msg });

  // Derived booleans
  const hasActiveStripe =
    Boolean(form.stripeSubscriptionId) &&
    ["active", "trialing"].includes(form.billingStatus) &&
    !form.stripeCancelAtPeriodEnd;

  const isTrialing = Boolean(form.stripeSubscriptionId) && form.billingStatus === "trialing";
  const hasAnyStripe = Boolean(form.stripeSubscriptionId);
  const subscribeUntilDate = form.subscribeUntil ? new Date(form.subscribeUntil) : null;
  const isSubFuture = subscribeUntilDate && subscribeUntilDate > new Date();
  const isPastDue = form.billingStatus === "past_due" && Boolean(form.stripeSubscriptionId);
  const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;
  const subTooFarAhead = subscribeUntilDate && (subscribeUntilDate.getTime() - Date.now()) > ONE_YEAR_MS;
  const showStripeSetup = !hasActiveStripe && !subTooFarAhead && form.accountStatus !== "toBeArchived";

  const fetchMe = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${config.BACKEND_BASE_URL}/api/admin/authentication/me`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = res.data?.data || {};
      const next = {
        name: data.name || "",
        parentCategory: data.parentCategory || "",
        accountStatus: data.accountStatus || "",
        subscribeUntil: data.subscribeUntil || "",
        billingStatus: data.billingStatus || "none",
        stripeSubscriptionId: data.stripeSubscriptionId || null,
        stripePriceId: data.stripePriceId || "",
        stripeCancelAtPeriodEnd: Boolean(data.stripeCancelAtPeriodEnd),
        currentPlanKey: data.currentPlanKey || null,
        pendingPlanKey: data.pendingPlanKey || null,
        pendingSwitchDate: data.pendingSwitchDate || null,
      };
      setForm(next);

      if (next.stripeSubscriptionId) {
        try {
          const invRes = await axios.get(
            `${config.BACKEND_BASE_URL}/api/admin/billing/invoices`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setInvoices(invRes.data?.invoices || []);
        } catch (_) {}
      }
    } catch (err) {
      notify("error", t("billing.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMe();
  }, []);

  useEffect(() => {
    if (!form.parentCategory) return;
    axios
      .get(`${config.BACKEND_BASE_URL}/api/affiliate-plans`)
      .then((res) => {
        const filtered = (res.data || []).filter(
          (p) => p.parentCategory === form.parentCategory
        );
        setPlans(filtered);
      })
      .catch(() => {});
  }, [form.parentCategory]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("renewal") === "success") {
      notify("success", t("billing.renewalSuccess"));
      window.history.replaceState({}, "", window.location.pathname);
      setTimeout(() => fetchMe(), 3000);
    } else if (params.get("renewal") === "cancelled") {
      notify("info", t("billing.renewalCancelled"));
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const handleRenew = async () => {
    try {
      setRenewing(true);
      const res = await axios.post(
        `${config.BACKEND_BASE_URL}/api/admin/billing/renew`,
        { planKey },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const checkoutUrl = res.data?.checkoutUrl;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      }
    } catch (err) {
      notify("error", err?.response?.data?.message || t("billing.stripeSessionError"));
      setRenewing(false);
    }
  };

  const handlePortal = async () => {
    try {
      setPortalLoading(true);
      const res = await axios.post(
        `${config.BACKEND_BASE_URL}/api/admin/billing/portal`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const url = res.data?.url;
      if (url) window.location.href = url;
    } catch (err) {
      notify("error", err?.response?.data?.message || t("billing.stripePortalError"));
      setPortalLoading(false);
    }
  };

  const handleInvoicePay = async () => {
    try {
      setInvoiceLoading(true);
      const res = await axios.get(
        `${config.BACKEND_BASE_URL}/api/admin/billing/invoice-pay-url`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const url = res.data?.url;
      if (url) window.location.href = url;
    } catch (err) {
      notify("error", err?.response?.data?.message || t("billing.invoiceError"));
      setInvoiceLoading(false);
    }
  };

  const handleSwitchPlan = async () => {
    if (!switchPlanKey) return;
    try {
      setSwitching(true);
      await axios.post(
        `${config.BACKEND_BASE_URL}/api/admin/billing/switch-plan`,
        { planKey: switchPlanKey },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      notify("success", t("billing.planSwitched"));
      setSwitchPlanKey(null);
      await fetchMe();
    } catch (err) {
      notify("error", err?.response?.data?.message || t("billing.planSwitchError"));
    } finally {
      setSwitching(false);
    }
  };

  const handleCancel = async () => {
    try {
      setCancelling(true);
      await axios.post(
        `${config.BACKEND_BASE_URL}/api/admin/billing/cancel-subscription`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCancelModalOpen(false);
      setForm((prev) => ({ ...prev, accountStatus: "toBeArchived" }));
      notify("success", t("billing.cancelSuccess"));
    } catch (err) {
      notify("error", err?.response?.data?.message || t("billing.cancelError"));
    } finally {
      setCancelling(false);
    }
  };

  const handleTerminate = async () => {
    try {
      setTerminating(true);
      await axios.post(
        `${config.BACKEND_BASE_URL}/api/admin/billing/terminate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTerminateModalOpen(false);
      setTerminateConfirmText("");
      clearToken();
      navigate("/authentication/sign-in", { replace: true });
    } catch (err) {
      notify("error", err?.response?.data?.message || t("billing.terminateError"));
    } finally {
      setTerminating(false);
    }
  };

  const accountStatusInfo = ACCOUNT_STATUS_LABELS[form.accountStatus] || { labelKey: null, color: "grey" };
  const billingStatusInfo = BILLING_STATUS_LABELS[form.billingStatus] || { labelKey: null, color: "grey" };
  const subType = hasAnyStripe ? t("billing.subTypePlatform") : t("billing.subTypeManual");

  const currentPlanKey = form.currentPlanKey || null;
  const currentPlan = plans.find((p) => p.planKey === currentPlanKey) || null;

  const graceDaysLeft = form.accountStatus === "grace" && subscribeUntilDate
    ? Math.max(0, Math.ceil(
        (subscribeUntilDate.getTime() + 7 * 24 * 60 * 60 * 1000 - Date.now()) / (24 * 60 * 60 * 1000)
      ))
    : null;

  return (
    <DashboardLayout>
      <Box py={3}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={10}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={3}>

            {/* ── Status ── */}
            <SectionCard title={t("billing.status.sectionTitle")}>
              <Stack>
                <InfoRow label={t("billing.status.labelSubType")} value={subType} />
                <InfoRow
                  label={t("billing.status.labelAccountStatus")}
                  badge={<StatusBadge label={accountStatusInfo.labelKey ? t(accountStatusInfo.labelKey) : form.accountStatus} color={accountStatusInfo.color} />}
                />
                {hasAnyStripe && (
                  <InfoRow
                    label={t("billing.status.labelBillingStatus")}
                    badge={<StatusBadge label={billingStatusInfo.labelKey ? t(billingStatusInfo.labelKey) : form.billingStatus} color={billingStatusInfo.color} />}
                  />
                )}
                <InfoRow
                  label={
                    form.accountStatus === "grace"
                      ? t("billing.status.labelExpiredAt")
                      : isTrialing
                      ? t("billing.status.labelFirstCharge")
                      : t("billing.status.labelValidUntil")
                  }
                  value={formatDate(form.subscribeUntil)}
                />
                {form.accountStatus === "grace" && graceDaysLeft !== null && (
                  <InfoRow
                    label={t("billing.status.labelGraceEnd")}
                    value={formatDate(new Date(subscribeUntilDate.getTime() + 7 * 24 * 60 * 60 * 1000))}
                  />
                )}
              </Stack>

              {form.accountStatus === "visible" && (
                <Alert
                  severity="success"
                  sx={{
                    mt: 2,
                    bgcolor: "rgba(76,175,80,0.1)",
                    color: "#fff",
                    border: "1px solid rgba(76,175,80,0.35)",
                    "& .MuiAlert-icon": { color: "success.main" },
                  }}
                >
                  {t("billing.subscriptionActive")}
                </Alert>
              )}

              {form.accountStatus === "toBeArchived" && (
                <Alert
                  severity="warning"
                  sx={{
                    mt: 2,
                    bgcolor: "rgba(255,152,0,0.12)",
                    color: "#fff",
                    border: "1px solid rgba(255,152,0,0.4)",
                    "& .MuiAlert-icon": { color: "warning.main" },
                  }}
                >
                  {t("billing.status.toBeArchivedAlert", { date: formatDate(form.subscribeUntil) })}
                </Alert>
              )}

              {form.accountStatus === "grace" && (
                <Alert
                  severity="warning"
                  sx={{
                    mt: 2,
                    bgcolor: "rgba(255,152,0,0.12)",
                    color: "#fff",
                    border: "1px solid rgba(255,152,0,0.4)",
                    "& .MuiAlert-icon": { color: "warning.main" },
                  }}
                >
                  {t("billing.status.graceAlert_before")}
                  <strong>{graceDaysLeft} {graceDaysLeft === 1 ? t("billing.status.graceDayOne") : t("billing.status.graceDayMany")}</strong>
                  {t("billing.status.graceAlert_after")}
                </Alert>
              )}

              {subTooFarAhead && !hasAnyStripe && (
                <Alert
                  severity="info"
                  sx={{
                    mt: 2,
                    bgcolor: "rgba(33,150,243,0.08)",
                    color: "#fff",
                    border: "1px solid rgba(33,150,243,0.3)",
                    "& .MuiAlert-icon": { color: "#64b5f6" },
                  }}
                >
                  {t("billing.status.stripeUnavailableAlert", { date: formatDate(form.subscribeUntil) })}
                </Alert>
              )}

              {form.accountStatus === "suspended" && (
                <Alert
                  severity="error"
                  sx={{
                    mt: 2,
                    bgcolor: "rgba(244,67,54,0.1)",
                    color: "#fff",
                    border: "1px solid rgba(244,67,54,0.35)",
                    "& .MuiAlert-icon": { color: "error.main" },
                  }}
                >
                  {t("billing.status.suspendedAlert")}
                </Alert>
              )}
            </SectionCard>

            {/* ── Stripe trialing info ── */}
            {isTrialing && !form.stripeCancelAtPeriodEnd && (
              <SectionCard title={t("billing.trialing.sectionTitle")}>
                <Alert
                  severity="success"
                  sx={{
                    bgcolor: "rgba(76,175,80,0.1)",
                    color: "#fff",
                    border: "1px solid rgba(76,175,80,0.35)",
                    "& .MuiAlert-icon": { color: "success.main" },
                  }}
                >
                  {t("billing.trialing.alert_before")}<strong>{formatDate(form.subscribeUntil)}</strong>{t("billing.trialing.alert_after")}
                </Alert>
                <Box mt={2}>
                  <Button
                    variant="outlined"
                    onClick={handlePortal}
                    disabled={portalLoading}
                    sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.4)" }}
                  >
                    {portalLoading ? <CircularProgress size={18} color="inherit" /> : t("billing.trialing.manageCard")}
                  </Button>
                </Box>
              </SectionCard>
            )}

            {/* ── Stripe setup (for admin subs or expired Stripe) ── */}
            {showStripeSetup && (
              <SectionCard
                title={isSubFuture ? t("billing.setup.titleSetup") : t("billing.setup.titleRenew")}
                subtitle={isSubFuture ? t("billing.setup.subtitleSetup") : t("billing.setup.subtitleRenew")}
              >
                <Stack spacing={3}>
                  {isSubFuture && (
                    <Alert
                      severity="info"
                      sx={{
                        bgcolor: "rgba(33,150,243,0.08)",
                        color: "#fff",
                        border: "1px solid rgba(33,150,243,0.3)",
                        "& .MuiAlert-icon": { color: "#64b5f6" },
                      }}
                    >
                      {t("billing.setup.currentSubAlert_before")}<strong>{formatDate(form.subscribeUntil)}</strong>{t("billing.setup.currentSubAlert_after")}
                    </Alert>
                  )}

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1} flexWrap="wrap">
                    {plans.map((planData) => (
                      <Button
                        key={planData.planKey}
                        variant={planKey === planData.planKey ? "contained" : "outlined"}
                        onClick={() => setPlanKey(planData.planKey)}
                        sx={{
                          color: "#fff",
                          borderColor: "rgba(255,255,255,0.4)",
                          flexDirection: "column",
                          minWidth: 140,
                          py: 1.5,
                        }}
                      >
                        <span>{planData.label}</span>
                        <Typography variant="caption" sx={{ opacity: 0.8, lineHeight: 1.2, mt: 0.25 }}>
                          {planData.amount} {planData.currency?.toUpperCase()}
                        </Typography>
                      </Button>
                    ))}
                  </Stack>

                  <Box>
                    <Button variant="contained" onClick={handleRenew} disabled={renewing}>
                      {renewing ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : isSubFuture ? (
                        t("billing.setup.btnSetup")
                      ) : (
                        t("billing.setup.btnRenew")
                      )}
                    </Button>
                  </Box>
                </Stack>
              </SectionCard>
            )}

            {/* ── Switch plan (active or trialing Stripe, not cancelling) ── */}
            {hasActiveStripe && plans.length >= 1 && (
              <SectionCard
                title={t("billing.switchPlan.sectionTitle")}
                subtitle={t("billing.switchPlan.subtitle")}
              >
                <Stack spacing={3}>
                  {currentPlan && (
                    <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)" }}>
                      {t("billing.switchPlan.currentPlanLabel")}{" "}
                      <strong style={{ color: "#fff" }}>
                        {currentPlan.label} — {currentPlan.amount} {currentPlan.currency?.toUpperCase()}
                      </strong>
                    </Typography>
                  )}

                  {form.pendingPlanKey && form.pendingSwitchDate && form.pendingPlanKey !== form.currentPlanKey && form.accountStatus !== "toBeArchived" && (
                    <Alert
                      severity="info"
                      sx={{
                        bgcolor: "rgba(33,150,243,0.08)",
                        color: "#fff",
                        border: "1px solid rgba(33,150,243,0.3)",
                        "& .MuiAlert-icon": { color: "#64b5f6" },
                      }}
                    >
                      {t("billing.switchPlan.pendingAlert", {
                        plan: plans.find((p) => p.planKey === form.pendingPlanKey)?.label || form.pendingPlanKey,
                        date: formatDate(form.pendingSwitchDate),
                      })}
                    </Alert>
                  )}

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1} flexWrap="wrap">
                    {plans.filter((p) => p.planKey !== currentPlanKey).map((planData) => {
                      const key = planData.planKey;
                      const isSelected = key === switchPlanKey;
                      return (
                        <Button
                          key={key}
                          variant={isSelected ? "contained" : "outlined"}
                          onClick={() => setSwitchPlanKey(key)}
                          sx={{
                            color: "#fff",
                            borderColor: "rgba(255,255,255,0.4)",
                            flexDirection: "column",
                            minWidth: 140,
                            py: 1.5,
                          }}
                        >
                          <span>{planData.label}</span>
                          <Typography variant="caption" sx={{ opacity: 0.8, lineHeight: 1.2, mt: 0.25 }}>
                            {planData.amount} {planData.currency?.toUpperCase()}
                          </Typography>
                        </Button>
                      );
                    })}
                  </Stack>

                  <Box>
                    <Button
                      variant="contained"
                      onClick={handleSwitchPlan}
                      disabled={switching || !switchPlanKey}
                    >
                      {switching ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : switchPlanKey ? (
                        t("billing.switchPlan.btnSwitch", { plan: plans.find((p) => p.planKey === switchPlanKey)?.label || switchPlanKey })
                      ) : "—"}
                    </Button>
                  </Box>
                </Stack>
              </SectionCard>
            )}

            {/* ── Active Stripe portal ── */}
            {hasAnyStripe && !isTrialing && (
              <SectionCard
                title={t("billing.portal.sectionTitle")}
                subtitle={t("billing.portal.subtitle")}
              >
                <Stack spacing={2}>
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)" }}>
                    {t("billing.portal.description")}
                  </Typography>
                  <Box>
                    <Button
                      variant="contained"
                      onClick={handlePortal}
                      disabled={portalLoading}
                    >
                      {portalLoading ? <CircularProgress size={20} color="inherit" /> : t("billing.portal.btn")}
                    </Button>
                  </Box>
                </Stack>
              </SectionCard>
            )}

            {/* ── Payment history ── */}
            {hasAnyStripe && invoices.length > 0 && (
              <SectionCard title={t("billing.invoices.sectionTitle")}>
                <Stack spacing={0}>
                  {invoices.map((inv) => {
                    const amount = inv.amountPaid
                      ? `${(inv.amountPaid / 100).toFixed(2)} ${inv.currency?.toUpperCase()}`
                      : "—";
                    const date = inv.created
                      ? new Date(inv.created * 1000).toLocaleDateString(dateLocale, { day: "2-digit", month: "long", year: "numeric" })
                      : "—";
                    const periodFrom = inv.periodStart
                      ? new Date(inv.periodStart * 1000).toLocaleDateString(dateLocale, { day: "2-digit", month: "short", year: "numeric" })
                      : null;
                    const periodTo = inv.periodEnd
                      ? new Date(inv.periodEnd * 1000).toLocaleDateString(dateLocale, { day: "2-digit", month: "short", year: "numeric" })
                      : null;

                    return (
                      <Box
                        key={inv.id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          py: 1.5,
                          borderBottom: "1px solid rgba(255,255,255,0.07)",
                          "&:last-child": { borderBottom: "none" },
                          gap: 2,
                          flexWrap: "wrap",
                        }}
                      >
                        <Box>
                          <Typography variant="body2" sx={{ color: "#fff", fontWeight: 600 }}>
                            {amount}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.45)" }}>
                            {date}
                            {periodFrom && periodTo ? ` · ${periodFrom} – ${periodTo}` : ""}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          {inv.hostedUrl && (
                            <Button
                              size="small"
                              variant="outlined"
                              href={inv.hostedUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ color: "rgba(255,255,255,0.7)", borderColor: "rgba(255,255,255,0.2)", fontSize: "0.72rem" }}
                            >
                              {t("billing.invoices.receipt")}
                            </Button>
                          )}
                          {inv.pdfUrl && (
                            <Button
                              size="small"
                              variant="outlined"
                              href={inv.pdfUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ color: "rgba(255,255,255,0.7)", borderColor: "rgba(255,255,255,0.2)", fontSize: "0.72rem" }}
                            >
                              PDF
                            </Button>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              </SectionCard>
            )}

            {/* ── Payment failed ── */}
            {isPastDue && (
              <SectionCard title={t("billing.paymentFailed.sectionTitle")}>
                <Alert
                  severity="error"
                  sx={{
                    mb: 2,
                    bgcolor: "rgba(244,67,54,0.1)",
                    color: "#fff",
                    border: "1px solid rgba(244,67,54,0.35)",
                    "& .MuiAlert-icon": { color: "error.main" },
                  }}
                >
                  {t("billing.paymentFailed.alert")}
                </Alert>
                <Box>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleInvoicePay}
                    disabled={invoiceLoading}
                  >
                    {invoiceLoading ? <CircularProgress size={20} color="inherit" /> : t("billing.paymentFailed.btn")}
                  </Button>
                </Box>
              </SectionCard>
            )}

            {/* ── Cancel / Terminate subscription ── */}
            {form.accountStatus === "visible" && (
              <SectionCard title={t("billing.cancel.sectionTitle")}>
                <Stack spacing={3}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <Box
                      sx={{
                        flex: 1,
                        p: 2,
                        border: "1px solid rgba(255,152,0,0.3)",
                        borderRadius: 2,
                        bgcolor: "rgba(255,152,0,0.05)",
                      }}
                    >
                      <Typography variant="body2" fontWeight={700} sx={{ color: "#ffb74d", mb: 0.5 }}>
                        {t("billing.cancel.nonRenewalTitle")}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.65)", mb: 2 }}>
                        {hasActiveStripe
                          ? t("billing.cancel.nonRenewalDescStripe")
                          : t("billing.cancel.nonRenewalDescManual", { date: formatDate(form.subscribeUntil) })}
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setCancelModalOpen(true)}
                        sx={{ color: "#ffb74d", borderColor: "rgba(255,152,0,0.5)" }}
                      >
                        {t("billing.cancel.nonRenewalBtn")}
                      </Button>
                    </Box>

                    <Box
                      sx={{
                        flex: 1,
                        p: 2,
                        border: "1px solid rgba(244,67,54,0.35)",
                        borderRadius: 2,
                        bgcolor: "rgba(244,67,54,0.05)",
                      }}
                    >
                      <Typography variant="body2" fontWeight={700} sx={{ color: "#e57373", mb: 0.5 }}>
                        {t("billing.cancel.immediateTitle")}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.65)", mb: 2 }}>
                        {hasActiveStripe
                          ? t("billing.cancel.immediateDescStripe")
                          : t("billing.cancel.immediateDescManual")}
                      </Typography>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => { setTerminateConfirmText(""); setTerminateModalOpen(true); }}
                        sx={{ borderColor: "rgba(244,67,54,0.5)" }}
                      >
                        {t("billing.cancel.immediateBtn")}
                      </Button>
                    </Box>
                  </Stack>
                </Stack>
              </SectionCard>
            )}

          </Stack>
        )}
      </Box>

      {/* Cancel dialog */}
      <Dialog
        open={cancelModalOpen}
        onClose={() => !cancelling && setCancelModalOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: "#1a1a2e",
            color: "#fff",
            border: "1px solid rgba(255,255,255,0.15)",
          },
        }}
      >
        <DialogTitle sx={{ color: "#fff" }}>{t("billing.cancel.dialogTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "rgba(255,255,255,0.75)" }}>
            {hasActiveStripe
              ? <>{t("billing.cancel.dialogDescStripe_before")}<strong style={{ color: "#fff" }}>{formatDate(form.subscribeUntil)}</strong>{t("billing.cancel.dialogDescStripe_after")}</>
              : <>{t("billing.cancel.dialogDescManual_before")}<strong style={{ color: "#fff" }}>{formatDate(form.subscribeUntil)}</strong>{t("billing.cancel.dialogDescManual_after")}</>}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setCancelModalOpen(false)}
            disabled={cancelling}
            sx={{ color: "rgba(255,255,255,0.7)" }}
          >
            {t("billing.cancel.dialogCancel")}
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={handleCancel}
            disabled={cancelling}
          >
            {cancelling ? <CircularProgress size={20} color="inherit" /> : t("billing.cancel.dialogConfirm")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Terminate dialog */}
      <Dialog
        open={terminateModalOpen}
        onClose={() => !terminating && setTerminateModalOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: "#1a1a2e",
            color: "#fff",
            border: "1px solid rgba(244,67,54,0.4)",
          },
        }}
      >
        <DialogTitle sx={{ color: "#e57373" }}>{t("billing.cancel.terminateDialogTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "rgba(255,255,255,0.75)", mb: 2 }}>
            {hasActiveStripe
              ? t("billing.cancel.terminateDialogDescStripe")
              : t("billing.cancel.terminateDialogDescManual")}
          </DialogContentText>
          <DialogContentText sx={{ color: "rgba(255,255,255,0.75)", mb: 1.5 }}>
            {t("billing.cancel.terminateDialogTypePrompt_before")}<strong style={{ color: "#e57373" }}>delete</strong>{t("billing.cancel.terminateDialogTypePrompt_after")}
          </DialogContentText>
          <input
            value={terminateConfirmText}
            onChange={(e) => setTerminateConfirmText(e.target.value)}
            placeholder="delete"
            style={{
              width: "100%",
              padding: "8px 12px",
              background: "rgba(255,255,255,0.07)",
              border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: 6,
              color: "#fff",
              fontSize: 14,
              outline: "none",
              boxSizing: "border-box",
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setTerminateModalOpen(false)}
            disabled={terminating}
            sx={{ color: "rgba(255,255,255,0.7)" }}
          >
            {t("billing.cancel.terminateDialogCancel")}
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={handleTerminate}
            disabled={terminating || terminateConfirmText.toLowerCase() !== "delete"}
          >
            {terminating ? <CircularProgress size={20} color="inherit" /> : t("billing.cancel.terminateDialogConfirm")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          severity={toast.type}
          variant="filled"
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}
